import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { labelOf } from '@/components/dashboards/format';
import { LoadingEffect } from '@/components/LoadingEffect';
import { PageTitle } from '@/components/PageTitle';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toaster';
import { errorText } from '@/lib/errors';
import { formatDateTime, formatNumber } from '@/lib/format';
import { requirePermission } from '@/lib/guards';
import { client, orpc } from '@/lib/orpc';
import { Link } from '@/lib/router-compat';

const CodeEditor = lazy(() => import('@/components/dashboards/builder/CodeEditor'));

export const Route = createFileRoute('/_app/admin/datasets')({
  beforeLoad: ({ context }) => requirePermission(context.me, 'datasets.manage'),
  component: DatasetsPage
});

type Row = Awaited<ReturnType<typeof client.datasets.list>>[number];

function DatasetsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const status = useQuery(orpc.datasets.status.queryOptions());
  const list = useQuery(orpc.datasets.list.queryOptions());
  const [editing, setEditing] = useState<{ id: string | null; text: string; enabled: boolean } | null>(null);
  const [deleting, setDeleting] = useState<Row | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => client.datasets.remove({ id }),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: orpc.datasets.key() });
    },
    onError: (e) => toast('error', errorText(t, e))
  });
  const bq = status.data?.bigquery;

  return (
    <div className="space-y-6 p-6">
      <PageTitle
        title={t('datasets.title')}
        items={[{ label: t('admin.section') }, { label: t('datasets.title'), active: true }]}
        actions={
          bq?.configured && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing({ id: null, text: '', enabled: true })}>
              <span className="iconify lucide--plus size-4" />
              {t('datasets.new')}
            </button>
          )
        }
      />

      <div className="card bg-base-100 shadow-sm">
        <div className="card-body gap-2 text-sm">
          <h2 className="card-title text-base">BigQuery</h2>
          {bq && !bq.configured && <p className="text-base-content/70">{t('datasets.notConfigured')}</p>}
          {bq?.configured && (
            <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2 xl:grid-cols-4">
              <div>
                <dt className="text-base-content/60">{t('datasets.project')}</dt>
                <dd className="font-mono">{bq.project}</dd>
              </div>
              <div>
                <dt className="text-base-content/60">{t('datasets.location')}</dt>
                <dd>{bq.location}</dd>
              </div>
              <div>
                <dt className="text-base-content/60">{t('datasets.maxBytes')}</dt>
                <dd>{formatNumber((bq.maxBytes ?? 0) / 1e9, 1)} GB</dd>
              </div>
              <div>
                <dt className="text-base-content/60">{t('datasets.credentials')}</dt>
                <dd>{t(`datasets.credentialKinds.${bq.credentials}`)}</dd>
              </div>
              <div className="sm:col-span-2 xl:col-span-4">
                <dt className="text-base-content/60">{t('datasets.allowed')}</dt>
                <dd className="font-mono text-xs">{bq.allowedDatasets.join(', ')}</dd>
              </div>
            </dl>
          )}
        </div>
      </div>

      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-0">
          <table className="table">
            <thead>
              <tr>
                <th>{t('datasets.dataset')}</th>
                <th>{t('datasets.source')}</th>
                <th>{t('datasets.usedBy')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data?.map((d) => {
                const def = d.definition as { label?: { de: string; en: string }; source?: { kind: string; relation?: string; table?: string } };
                return (
                  <tr key={d.id} className="align-top">
                    <td>
                      <p className="font-medium">{def.label ? labelOf(def.label) : d.id}</p>
                      <p className="text-base-content/50 font-mono text-xs">{d.id}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {d.builtin && <span className="badge badge-ghost badge-xs">{t('datasets.builtin')}</span>}
                        {!d.enabled && <span className="badge badge-warning badge-soft badge-xs">{t('datasets.disabled')}</span>}
                        {d.issues.length > 0 && <span className="badge badge-error badge-soft badge-xs" title={d.issues.join('\n')}>{t('datasets.invalid')}</span>}
                      </div>
                    </td>
                    <td className="font-mono text-xs">{def.source?.table ?? def.source?.relation}</td>
                    <td className="text-sm">
                      {d.usedBy.length === 0
                        ? '–'
                        : d.usedBy.map((u) => (
                            <Link key={u.id} to={`/dashboards/${u.id}`} className="link link-hover block">
                              {u.title}
                            </Link>
                          ))}
                    </td>
                    <td className="text-end whitespace-nowrap">
                      {!d.builtin && (
                        <>
                          <span className="text-base-content/50 me-2 text-xs">{formatDateTime(d.updatedAt)}</span>
                          <button type="button" className="btn btn-ghost btn-xs" onClick={() => setEditing({ id: d.id, text: JSON.stringify(d.definition, null, 2), enabled: d.enabled })}>
                            {t('dashboards.edit')}
                          </button>
                          <button type="button" className="btn btn-ghost btn-xs text-error" onClick={() => setDeleting(d)}>
                            {t('admin.deleteRole')}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <DatasetEditor initial={editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        danger
        busy={remove.isPending}
        text={deleting && t('datasets.confirmDelete', { id: deleting.id })}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate(deleting!.id)}
      />
    </div>
  );
}

function DatasetEditor({ initial, onClose }: { initial: { id: string | null; text: string; enabled: boolean }; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const schema = useQuery({ ...orpc.datasets.schema.queryOptions(), staleTime: Infinity });
  const [text, setText] = useState(initial.text);
  const [table, setTable] = useState('');
  const [enabled, setEnabled] = useState(initial.enabled);
  const [issues, setIssues] = useState<{ path: string; message: string }[]>([]);

  const inspect = useMutation({
    mutationFn: () => client.datasets.inspect({ table: table.trim() }),
    onSuccess: (r) => setText(JSON.stringify(r.suggestion, null, 2)),
    onError: (e) => toast('error', errorText(t, e))
  });
  const save = useMutation({
    mutationFn: async () => {
      // A JSON syntax error surfaces as a SyntaxError toast (onError).
      const definition = JSON.parse(text) as { id?: unknown };
      return client.datasets.save({ id: String(initial.id ?? definition.id ?? ''), definition, enabled });
    },
    onSuccess: async () => {
      toast('success', t('crm.saved'));
      await queryClient.invalidateQueries({ queryKey: orpc.datasets.key() });
      await queryClient.invalidateQueries({ queryKey: orpc.dashboards.catalog.key() });
      onClose();
    },
    onError: (e) => {
      const data = (e as { data?: { issues?: { path: string; message: string }[] } }).data;
      if (data?.issues) setIssues(data.issues);
      else toast('error', e instanceof SyntaxError || !('code' in (e as object)) ? (e as Error).message : errorText(t, e));
    }
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={initial.id ? t('datasets.editTitle', { id: initial.id }) : t('datasets.new')}
      actions={
        <>
          <label className="me-auto flex cursor-pointer items-center gap-2 text-sm">
            <input type="checkbox" className="toggle toggle-sm" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            {t('datasets.enabled')}
          </label>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>
            {t('admin.cancel')}
          </button>
          <button type="button" className="btn btn-sm btn-primary" disabled={!text.trim() || save.isPending} onClick={() => save.mutate()}>
            {t('crm.save')}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {!initial.id && (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-1 flex-col gap-1 text-sm">
              <span className="text-base-content/60 text-xs">{t('datasets.table')}</span>
              <input className="input input-sm w-full font-mono" placeholder="project.dataset.table" value={table} onChange={(e) => setTable(e.target.value)} />
            </label>
            <button type="button" className="btn btn-sm" disabled={!table.trim() || inspect.isPending} onClick={() => inspect.mutate()}>
              {t('datasets.loadColumns')}
            </button>
          </div>
        )}
        <p className="text-base-content/60 text-xs">{t('datasets.editorHint')}</p>
        {issues.length > 0 && (
          <ul className="text-error list-disc ps-5 font-mono text-xs">
            {issues.map((i) => (
              <li key={`${i.path}:${i.message}`}>
                {i.path || '/'}: {i.message}
              </li>
            ))}
          </ul>
        )}
        <div className="h-[55vh]">
          <Suspense fallback={<LoadingEffect height="100%" className="h-full w-full" />}>
            <CodeEditor document="datasets/definition.json" value={text} onChange={setText} schema={schema.data} />
          </Suspense>
        </div>
      </div>
    </Modal>
  );
}
